import type { PartialMenuItemStoryblok } from '@/storyblok/wirfuersklima/types';

type NavLevel0 = {
  _uid: string;
  label: string;
  link?: unknown;
  component?: string;
};

/**
 * The campaign header uses the same `components/header` story as the main site, but Storyblok
 * content can be `header` (field `menu`) or `global_header` (field `nav_menu`), and editors may
 * use `Menu` casing. The overlay `<nav>` must always be mounted so the hamburger has a real target.
 */
export function getCampaignHeaderMenuFromBlok(content: Record<string, unknown>): PartialMenuItemStoryblok[] {
  const fromMenu = content['menu'];
  if (Array.isArray(fromMenu) && isPartialMenuItemList(fromMenu)) {
    return fromMenu;
  }
  const fromPascal = content['Menu'];
  if (Array.isArray(fromPascal) && isPartialMenuItemList(fromPascal)) {
    return fromPascal;
  }

  const component = content['component'];
  const navMenu = content['nav_menu'];
  if (component === 'global_header' && Array.isArray(navMenu)) {
    return flattenGlobalHeaderNavToMenuItems(navMenu);
  }
  if (Array.isArray(navMenu)) {
    // Fall back: some spaces only expose `nav_menu`
    return flattenGlobalHeaderNavToMenuItems(navMenu);
  }

  return [];
}

function isPartialMenuItemList(arr: unknown[]): arr is PartialMenuItemStoryblok[] {
  if (arr.length === 0) return true;
  return arr.every((x) => {
    if (x == null || typeof x !== 'object') return false;
    const o = x as { _uid?: string; label?: string; link?: unknown; component?: string };
    if (typeof o._uid !== 'string') return false;
    // `wfk_partial_menu_item` in Storyblok; also allow link-only or label-only entries from editors
    return o.component === 'wfk_partial_menu_item' || o.label != null || o.link != null;
  });
}

function flattenGlobalHeaderNavToMenuItems(navMenu: unknown[]): PartialMenuItemStoryblok[] {
  const out: PartialMenuItemStoryblok[] = [];
  for (const row of navMenu) {
    if (!row || typeof row !== 'object') continue;
    const item = row as NavLevel0;
    if (typeof item._uid !== 'string' || typeof item.label !== 'string') continue;
    out.push({
      _uid: item._uid,
      component: 'wfk_partial_menu_item',
      label: item.label,
      link: item.link as PartialMenuItemStoryblok['link'],
    });
  }
  return out;
}
